import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from "react";
import { View, StyleSheet } from "react-native";
import Svg, { Path, Defs, LinearGradient, Stop } from "react-native-svg";
import { colors, layout } from "@/constants";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
  runOnUI
} from "react-native-reanimated";

const waveHeight = 40;

// Single speed control for entire animation
const ANIMATION_SPEED = 1.5; // 1.0 = normal speed, 0.5 = half speed, 2.0 = double speed

// Pre-calculate wave configurations for realistic ocean swell
const WAVE_CONFIGS = [
  {
    frequency: 2, // Completes 1 cycle - long ocean swell
    amplitude: 0.67, // 2/3 of max height (20px out of 30px)
    phase: 0, // No phase for primary wave
    speedMultiplier: 1.0, // Fastest - baseline speed
    stroke: "hsl(220, 96.00%, 61.20%)",
    strokeWidth: 2,
    strokeOpacity: 0.3
  },
  {
    frequency: 4, // Completes 2 cycles - medium swell
    amplitude: 0.5, // 1/2 of max height (15px out of 30px)
    phase: Math.PI * 0.5, // Quarter cycle phase for natural phase difference
    speedMultiplier: 0.7, // Medium speed
    stroke: "hsl(109, 43.00%, 40.60%)", // Turquoise for soothing effect
    strokeWidth: 2,
    strokeOpacity: 0.3
  },
  {
    frequency: 6, // Completes 3 cycles - surface waves
    amplitude: 0.23, // ~1/4 of max height (7px out of 30px)
    phase: Math.PI * 1.0, // Half cycle phase for different phase
    speedMultiplier: 0.5, // Slowest
    stroke: "hsl(250, 48.80%, 68.60%)", // Medium purple for depth
    strokeWidth: 2,
    strokeOpacity: 0.3
  }
];

// Animation timing configuration for realistic ocean motion
const ANIMATION_CONFIG = {
  translation: {
    duration: 12000 / ANIMATION_SPEED, // Very slow - 12 seconds for realistic ocean pace
    easing: Easing.linear
  },
  amplitude: {
    scaleMax: 1.2, // Very subtle amplitude changes for gentle swell
    scaleMin: 0.8,
    scaleFlat: 0, // For mount/unmount transitions
    duration: 6000 / ANIMATION_SPEED, // Slow, natural amplitude pulsing
    easing: Easing.inOut(Easing.sin),
    mountDuration: 1500 / ANIMATION_SPEED, // Gentle mount/unmount
    mountEasing: Easing.out(Easing.quad)
  }
};

// Generate a smooth sine wave as cubic Bezier segments. Using Bezier curves
// (vs many short line segments) avoids per-vertex rendering artifacts that
// Safari exhibits on animated/transformed SVG paths.
const generateWavePath = (
  frequency: number,
  amplitudeFraction: number,
  waveWidth: number,
  phase: number = 0
) => {
  // Leave 1px margin on top and bottom to prevent clipping
  const maxAmplitude = (waveHeight - 2) / 2;
  const actualAmplitude = amplitudeFraction * maxAmplitude;
  const midY = waveHeight / 2;

  // Angular frequency in radians per pixel
  const omega = (2 * Math.PI * frequency) / waveWidth;

  // Sample 8 points per wavelength (every π/4). Cubic Hermite-to-Bezier
  // conversion with exact sine tangents is visually indistinguishable from
  // the true sine curve at this density.
  const samplesPerWavelength = 8;
  const totalSamples = Math.max(samplesPerWavelength * frequency, 16);
  const dx = waveWidth / totalSamples;

  const sineY = (x: number) =>
    midY + Math.sin(omega * x + phase) * actualAmplitude;
  const sineDy = (x: number) =>
    Math.cos(omega * x + phase) * actualAmplitude * omega;

  const parts: string[] = [`M${0},${sineY(0)}`];
  for (let i = 0; i < totalSamples; i++) {
    const x0 = i * dx;
    const x1 = (i + 1) * dx;
    const y0 = sineY(x0);
    const y1 = sineY(x1);
    const dy0 = sineDy(x0);
    const dy1 = sineDy(x1);
    // Hermite cubic -> Bezier: control handles at 1/3 of segment length
    const cp1x = x0 + dx / 3;
    const cp1y = y0 + (dy0 * dx) / 3;
    const cp2x = x1 - dx / 3;
    const cp2y = y1 - (dy1 * dx) / 3;
    parts.push(`C${cp1x},${cp1y} ${cp2x},${cp2y} ${x1},${y1}`);
  }
  return parts.join(" ");
};

export default function ChatAnimation() {
  // Container width state - starts with layout maxWidth and gets updated by onLayout
  const [containerWidth, setContainerWidth] = useState(layout.maxWidth);
  const waveWidth = containerWidth * 2;

  // Animation values - separate translateX for each wave
  const translateX1 = useSharedValue(0);
  const translateX2 = useSharedValue(0);
  const translateX3 = useSharedValue(0);
  const translationValues = [translateX1, translateX2, translateX3];
  const scaleY = useSharedValue(ANIMATION_CONFIG.amplitude.scaleFlat); // Start flat

  // Animated styles for each wave
  const animatedStyle1 = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX1.value }, { scaleY: scaleY.value }]
  }));
  const animatedStyle2 = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX2.value }, { scaleY: scaleY.value }]
  }));
  const animatedStyle3 = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX3.value }, { scaleY: scaleY.value }]
  }));
  const animatedStyles = [animatedStyle1, animatedStyle2, animatedStyle3];

  // Memoize wave paths to prevent recalculation on every render
  const wavePaths = useMemo(() => {
    return WAVE_CONFIGS.map(({ frequency, amplitude, phase }) =>
      generateWavePath(frequency, amplitude, waveWidth, phase)
    );
  }, [waveWidth]);

  // Animates waves by moving them left half a wave width in a loop.
  const startTranslationAnimation = () => {
    // Reset any existing animations
    translationValues.forEach((translateValue) => {
      translateValue.value = 0;
    });

    translationValues.forEach((translateValue, index) => {
      const config = WAVE_CONFIGS[index];
      translateValue.value = withRepeat(
        withTiming(-waveWidth / 2, {
          duration:
            ANIMATION_CONFIG.translation.duration / config.speedMultiplier,
          easing: ANIMATION_CONFIG.translation.easing
        }),
        -1,
        false
      );
    });
  };

  // Animates waves amplitudes by starting them at a flat amplitude and then pulsing them up and down.
  const startScaleAnimation = () => {
    // Reset any existing animations
    scaleY.value = ANIMATION_CONFIG.amplitude.scaleFlat;

    // First, transition amplitude from flat to normal
    scaleY.value = withTiming(
      ANIMATION_CONFIG.amplitude.scaleMin,
      {
        duration: ANIMATION_CONFIG.amplitude.mountDuration,
        easing: ANIMATION_CONFIG.amplitude.mountEasing
      },
      (finished) => {
        // After mount transition, start the repeating amplitude pulsing
        if (finished) {
          scaleY.value = withRepeat(
            withTiming(ANIMATION_CONFIG.amplitude.scaleMax, {
              duration: ANIMATION_CONFIG.amplitude.duration,
              easing: ANIMATION_CONFIG.amplitude.easing
            }),
            -1,
            true
          );
        }
      }
    );
  };

  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced handler for container width changes
  const handleLayout = useCallback(
    (event: any) => {
      const { width } = event.nativeEvent.layout;
      if (width > 0 && Math.abs(width - containerWidth) > 10) {
        if (debounceTimeout.current !== null) {
          clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
          setContainerWidth(width);
        }, 100);
      }
    },
    [containerWidth]
  );

  // Start animations on mount and on container width changes
  useEffect(() => {
    runOnUI(() => {
      startScaleAnimation();
      startTranslationAnimation();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth]);

  return (
    <View style={styles.container} onLayout={handleLayout}>
      {/* Render each wave with its own animation */}
      {WAVE_CONFIGS.map((config, index) => {
        return (
          <Animated.View
            key={index}
            style={[StyleSheet.absoluteFill, animatedStyles[index]]}
          >
            <Svg width={waveWidth} height={waveHeight}>
              <Path
                d={wavePaths[index]}
                fill="none"
                stroke={config.stroke}
                strokeWidth={config.strokeWidth}
                strokeOpacity={config.strokeOpacity}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </Animated.View>
        );
      })}

      {/* Static gradient overlay that doesn't move with waves */}
      <Svg
        width={containerWidth}
        height={waveHeight}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      >
        <Defs>
          <LinearGradient
            id="staticFadeGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <Stop offset="0%" stopColor={colors.surface} stopOpacity="1" />
            <Stop offset="15%" stopColor={colors.surface} stopOpacity="0" />
            <Stop offset="85%" stopColor={colors.surface} stopOpacity="0" />
            <Stop offset="100%" stopColor={colors.surface} stopOpacity="1" />
          </LinearGradient>
        </Defs>
        <Path
          d={`M0,0 L${containerWidth},0 L${containerWidth},${waveHeight} L0,${waveHeight} Z`}
          fill="url(#staticFadeGradient)"
        />
      </Svg>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: waveHeight,
    overflow: "hidden",
    backgroundColor: "transparent",
    justifyContent: "center",
    marginTop: 18
  }
});
